import { NpcLaunchButton } from '@/components/admin/npc/launch/launch-button';
import { NpcBenchEditor } from '@/components/admin/npc/npc-bench-editor';
import { NpcBenchesList } from '@/components/admin/npc/npc-benches-list';
import { NpcCardExtra, NpcCardIdentity, NpcCardPills } from '@/components/admin/npc/npc-card';
import { NpcFilterFields } from '@/components/admin/npc/npc-filter-fields';
import { NpcSeriesTable } from '@/components/admin/npc/npc-series-table';
import { NpcTabStrip } from '@/components/admin/npc/npc-tab-strip';
import { FilterBar } from '@/components/admin/versioned/filter-bar';
import { FilterPanel } from '@/components/admin/versioned/filter-panel';
import { FilterSummary } from '@/components/admin/versioned/filter-summary';
import { VersionedGrid } from '@/components/admin/versioned/versioned-grid';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import {
  createNpcBench,
  duplicateNpc,
  fetchNpcBenches,
  fetchNpcSeries,
  fetchNpcs,
  restoreNpc,
  restoreNpcBench,
  retireNpcBench,
  saveNpcBench,
} from '@/lib/api';
import {
  type NpcFilters,
  activeNpcFilters,
  filterNpcs,
  hasActiveNpcFilters,
  hasHiddenNpcFilter,
  npcAuthors,
  npcCounts,
  npcFiltersOf,
  npcSearchOf,
  resetNpcFilters,
} from '@/lib/npc-filters';
import { npcPlayedSentence, npcRefusalSentence } from '@/lib/npc-labels';
import {
  NPC_BENCH_NEW,
  type NpcLibrarySearch,
  type NpcLibraryTab,
  libraryTabOf,
} from '@/lib/npc-search';
import { NPC_WORDING } from '@/lib/npc-wording';
import { cn } from '@/lib/utils';
import { useVersionedDuplication } from '@/lib/versioned-duplication';
import { VERSIONED_SORT_LABEL, versionedRefusal, versionedRefusalOf } from '@/lib/versioned-labels';
import type { NpcBench, NpcCard } from '@matter_extractor/shared';
import { npcSortSchema } from '@matter_extractor/shared';
import { Alert, Checkbox, Icon, Spinner, buttonVariants } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * La section NPC (npc.md §12, issue #210): the h1, its sentence, « Nouveau NPC », and the three tabs
 * NPC, Bancs and Séries - which live in the URL (`?onglet=`), so « les rejeux de ce banc » is a
 * link somebody can paste.
 *
 * One component for the three tabs and not three routes: they share a head, a count each and the
 * queries that fill the counts, and a tab that lives in the address bar changes what a page shows,
 * not which page it is. It is also what leaves `/admin/npc/series/<id>` and
 * `/admin/npc/regarder/<id>` free for the page of a series and for « Regarder ».
 *
 * ## Everything a library does, the layer does
 *
 * The NPC tab is the persona's library on the same components of `components/admin/versioned/`: the
 * filters live in the URL and REPLACE rather than push, the whole library is one cache entry
 * filtered in the page, a card restores and duplicates from its own menu. What an NPC changes is
 * what `NPC_WORDING.developmentOnly` and the three own filters say: no tag filter, no production
 * pill, no dashed card - an NPC is created from the head's button or from a finished session.
 *
 * ## The two other tabs are not versioned
 *
 * A bench is a name and an ordered list, written whole (H4); the series are listed here (§9), each a link to
 * its page, and a bench launches from its row. Each tab names its own error: a Séries read that fails says so, and
 * never reads as « aucune série ».
 */

const INTRO =
  'Les personnes que l’on fait jouer à la place d’une vraie : un célèbre et ses propres mots, un profil écrit, une session réelle. Chacun se lance sur une expérience, seul ou dans un banc.';

export interface NpcLibraryProps {
  search: NpcLibrarySearch;
}

export function NpcLibrary({ search }: NpcLibraryProps) {
  const { email } = useAdminSession();
  const tab = libraryTabOf(search);

  const npcs = useQuery({ queryKey: ['npcs', email], queryFn: fetchNpcs, retry: false });
  const benches = useQuery({
    queryKey: ['npc-benches', email],
    queryFn: fetchNpcBenches,
    retry: false,
  });
  const series = useQuery({
    queryKey: ['npc-series', email],
    queryFn: fetchNpcSeries,
    retry: false,
  });
  const rejected = useRejectOn401(npcs.error);

  const liveNpcs = npcs.data?.npcs.filter((card) => card.retiredAt === null).length;
  const liveBenches = benches.data?.benches.filter((bench) => bench.retiredAt === null).length;

  return (
    <section className="space-y-5 pb-16">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="space-y-1">
          {/* `/^NPC$/` is what the e2e suites and the capture read at level 1. */}
          <h1 className="font-bold text-bleu-nuit text-h2">NPC</h1>
          <p className="max-w-[95ch] text-gray-600 text-small">{INTRO}</p>
        </div>
        <Link
          to="/admin/npc/nouveau"
          data-testid="npc-nouveau"
          className={cn(buttonVariants({ variant: 'secondary' }), 'no-underline')}
        >
          <Icon name="add" size={20} />
          Nouveau NPC
        </Link>
      </header>

      <NpcTabStrip<NpcLibraryTab>
        label="Sections des NPC"
        active={tab}
        tabs={[
          { id: 'npc', label: 'NPC', count: liveNpcs },
          { id: 'bancs', label: 'Bancs', count: liveBenches },
          { id: 'series', label: 'Séries', count: series.data?.series.length },
        ]}
        hrefOf={(entry) => (entry.id === 'npc' ? '/admin/npc' : `/admin/npc?onglet=${entry.id}`)}
        renderTab={(entry, props) => (
          <Link
            to="/admin/npc"
            search={entry.id === 'npc' ? {} : { onglet: entry.id }}
            activeOptions={{ exact: true }}
            className={props.className}
          >
            {props.children}
          </Link>
        )}
      />

      {tab === 'npc' ? (
        <NpcTab
          search={search}
          cards={npcs.data?.npcs}
          pending={npcs.isPending}
          failed={npcs.isError && !rejected}
        />
      ) : null}
      {tab === 'bancs' ? (
        <BenchesTab
          banc={search.banc}
          benches={benches.data?.benches}
          pending={benches.isPending}
          failed={benches.isError}
          cards={npcs.data?.npcs}
        />
      ) : null}
      {tab === 'series' ? (
        <div data-testid="npc-onglet-series">
          <NpcSeriesTable
            series={series.isPending ? undefined : series.isError ? null : series.data}
            renderName={(row, name) => (
              <Link
                to="/admin/npc/series/$id"
                params={{ id: row.id }}
                data-testid="npc-serie-lien"
                className="font-semibold text-bleu-nuit underline-offset-2 hover:underline"
              >
                {name}
              </Link>
            )}
          />
        </div>
      ) : null}
    </section>
  );
}

/* ---------------------------------------------------------------- the NPC tab */

interface NpcTabProps {
  search: NpcLibrarySearch;
  cards: readonly NpcCard[] | undefined;
  pending: boolean;
  failed: boolean;
}

function NpcTab({ search, cards: loaded, pending, failed }: NpcTabProps) {
  const { email } = useAdminSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const filters = npcFiltersOf(search);
  const [expanded, setExpanded] = useState(() => hasHiddenNpcFilter(filters));

  const restore = useMutation({
    mutationFn: (card: NpcCard) => restoreNpc(card.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['npcs', email] }),
  });
  const duplicate = useVersionedDuplication<NpcCard>({
    duplicate: duplicateNpc,
    listKey: 'npcs',
    onCopy: (npcId) =>
      void navigate({ to: '/admin/npc/$npcId', params: { npcId }, search: { copie: true } }),
  });

  /** One dimension at a time, merged into the address bar - and REPLACED there. */
  const change = (patch: Partial<NpcFilters>) =>
    navigate({ to: '/admin/npc', search: npcSearchOf({ ...filters, ...patch }), replace: true });

  const cards = loaded ?? [];
  const counts = npcCounts(cards, filters);
  const chips = activeNpcFilters(filters);
  const restoreRefused = versionedRefusalOf(restore.error);
  const duplicateRefused = versionedRefusalOf(duplicate.error);

  if (pending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Chargement des NPC" />
      </div>
    );
  }
  if (failed) {
    return <p className="text-gray-700">La bibliothèque de NPC n’a pas pu être chargée.</p>;
  }
  // The first time: nothing to filter and nothing to search. The way in is said, twice.
  if (cards.length === 0) {
    return (
      <div
        data-testid="npcs-vide"
        className="flex flex-col items-center gap-3 rounded-md border-2 border-gray-300 border-dashed px-6 py-14 text-center"
      >
        <h2 className="font-semibold text-body-lg text-gray-900">Aucun NPC pour l’instant</h2>
        <p className="max-w-prose text-gray-600 text-small">
          Un NPC est une personne que l’on fait jouer : ses sources, sa façon de jouer, ce qu’il
          éprouve. Créez-le ici, ou depuis une session finie avec « Créer un NPC » : son transcript
          devient sa source.
        </p>
        <Link
          to="/admin/npc/nouveau"
          className={cn(buttonVariants({ variant: 'secondary' }), 'no-underline')}
        >
          Nouveau NPC
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <FilterBar
        wording={NPC_WORDING}
        filters={filters}
        authors={npcAuthors(cards, filters)}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((was) => !was)}
        renderReferents={<NpcFilterFields filters={filters} onChange={change} />}
        onChange={change}
      />

      {expanded ? (
        <FilterPanel
          wording={NPC_WORDING}
          filters={filters}
          ownFilters={null}
          retiredCount={counts.retired}
          onChange={change}
          onReset={() =>
            navigate({
              to: '/admin/npc',
              search: npcSearchOf(resetNpcFilters(filters)),
              replace: true,
            })
          }
          resetDisabled={!hasActiveNpcFilters(filters)}
        />
      ) : null}

      <FilterSummary
        wording={NPC_WORDING}
        chips={chips}
        counts={counts}
        sort={filters.sort}
        sortOptions={npcSortSchema.options}
        sortLabel={VERSIONED_SORT_LABEL}
        onChange={change}
      />

      {duplicate.isError ? (
        <div data-testid="npcs-duplication-refusee">
          <Alert tone="danger">
            {duplicateRefused === null
              ? 'La copie n’a pas pu être écrite. Réessayez dans un instant.'
              : versionedRefusal(duplicateRefused, NPC_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}
      {restore.isError ? (
        <div data-testid="npcs-restauration-refusee">
          <Alert tone="danger">
            {restoreRefused === null
              ? (npcRefusalSentence(restore.error) ??
                'La restauration a échoué. Réessayez dans un instant.')
              : versionedRefusal(restoreRefused, NPC_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}

      <VersionedGrid
        wording={NPC_WORDING}
        cards={filterNpcs(cards, filters)}
        emptyMessage={
          chips.length === 0
            ? 'Aucun NPC à montrer : les NPC retirés se montrent par « Afficher les retirés ».'
            : 'Aucun NPC ne répond à ces filtres. Retirez une pastille, ou réinitialisez.'
        }
        facetsOf={(card) => [npcPlayedSentence(card.replayCount)]}
        renderIdentity={(card) => <NpcCardIdentity card={card} />}
        renderExtra={(card) => <NpcCardExtra card={card} />}
        renderPills={(card) => <NpcCardPills card={card} />}
        renderCardLink={(card, props) => (
          <Link to="/admin/npc/$npcId" params={{ npcId: card.id }} className={props.className}>
            {props.children}
          </Link>
        )}
        onRestore={(card) => restore.mutate(card)}
        restoringId={restore.isPending ? (restore.variables?.id ?? null) : null}
        onDuplicate={(card, tag) => duplicate.mutate({ card, tag })}
        duplicatingId={duplicate.isPending ? (duplicate.variables?.card.id ?? null) : null}
      />
    </div>
  );
}

/* ------------------------------------------------------------- the Bancs tab */

interface BenchesTabProps {
  banc: string | undefined;
  benches: readonly NpcBench[] | undefined;
  pending: boolean;
  failed: boolean;
  cards: readonly NpcCard[] | undefined;
}

function BenchesTab({ banc, benches, pending, failed, cards }: BenchesTabProps) {
  const { email } = useAdminSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRetired, setShowRetired] = useState(false);

  const toList = () =>
    void navigate({ to: '/admin/npc', search: { onglet: 'bancs' }, replace: true });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['npc-benches', email] });
    // The NPC cards do not carry their benches, but a deleted bench changes « Dans les bancs ».
    void queryClient.invalidateQueries({ queryKey: ['npc-referents'] });
  };

  const save = useMutation({
    mutationFn: (input: { id: string | null; write: { name: string; npcIds: string[] } }) =>
      input.id === null ? createNpcBench(input.write) : saveNpcBench(input.id, input.write),
    onSuccess: () => {
      refresh();
      toList();
    },
  });
  const retire = useMutation({
    mutationFn: (id: string) => retireNpcBench(id),
    onSuccess: () => {
      refresh();
      toList();
    },
  });
  const restore = useMutation({
    mutationFn: (bench: NpcBench) => restoreNpcBench(bench.id),
    onSuccess: refresh,
  });

  if (pending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Chargement des bancs" />
      </div>
    );
  }
  if (failed || benches === undefined) {
    return <p className="text-gray-700">Les bancs n’ont pas pu être chargés.</p>;
  }

  if (banc !== undefined) {
    const bench =
      banc === NPC_BENCH_NEW ? null : (benches.find((one) => one.id === banc) ?? undefined);
    if (bench === undefined) {
      return (
        <p data-testid="npc-banc-introuvable" className="text-gray-700">
          Ce banc n’existe pas.{' '}
          <Link
            to="/admin/npc"
            search={{ onglet: 'bancs' }}
            className="text-bleu-nuit underline underline-offset-2"
          >
            Revenir aux bancs
          </Link>
          .
        </p>
      );
    }
    return (
      <div className="max-w-2xl space-y-4">
        <p className="text-gray-600 text-small">
          <Link
            to="/admin/npc"
            search={{ onglet: 'bancs' }}
            className="text-bleu-nuit underline underline-offset-2"
          >
            Bancs
          </Link>
          <span className="mx-2">›</span>
          {bench === null ? 'Nouveau banc' : bench.name}
        </p>
        <NpcBenchEditor
          key={bench?.id ?? NPC_BENCH_NEW}
          bench={bench}
          cards={cards}
          busy={save.isPending || retire.isPending}
          error={
            save.isError
              ? (npcRefusalSentence(save.error) ??
                'Le banc n’a pas pu être enregistré. Réessayez dans un instant.')
              : retire.isError
                ? 'Le banc n’a pas pu être retiré. Réessayez dans un instant.'
                : null
          }
          onSave={(write) => save.mutate({ id: bench?.id ?? null, write })}
          onRetire={() => {
            if (bench !== null) retire.mutate(bench.id);
          }}
        />
      </div>
    );
  }

  const retiredCount = benches.filter((bench) => bench.retiredAt !== null).length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {retiredCount === 0 ? (
          <span />
        ) : (
          <Checkbox
            data-testid="npc-bancs-retires"
            checked={showRetired}
            onCheckedChange={setShowRetired}
            label={
              <>
                Afficher les retirés <span className="text-gray-500">({retiredCount})</span>
              </>
            }
          />
        )}
        <Link
          to="/admin/npc"
          search={{ onglet: 'bancs', banc: NPC_BENCH_NEW }}
          data-testid="npc-banc-nouveau"
          className={cn(
            buttonVariants({ variant: 'accent', size: 'sm', compact: true }),
            'no-underline',
          )}
        >
          Nouveau banc
        </Link>
      </div>
      {restore.isError ? (
        <div data-testid="npc-banc-restauration-refusee">
          <Alert tone="danger">
            {npcRefusalSentence(restore.error) ??
              'Le banc n’a pas pu être restauré. Réessayez dans un instant.'}
          </Alert>
        </div>
      ) : null}
      <NpcBenchesList
        benches={benches}
        showRetired={showRetired}
        restoringId={restore.isPending ? (restore.variables?.id ?? null) : null}
        onRestore={(bench) => restore.mutate(bench)}
        renderLaunch={(bench) => (
          <NpcLaunchButton
            target={{
              kind: 'bench',
              benchId: bench.id,
              name: bench.name,
              npcNames: bench.npcs.filter((member) => !member.retired).map((member) => member.name),
            }}
            label="Lancer"
            data-testid="npc-banc-lancer"
          />
        )}
      />
    </div>
  );
}
